import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

interface Member {
  id: string;
  full_name: string;
  user_id: string | null;
}

interface RoleWithMembers {
  role: string;
  members: { id: string; full_name: string }[];
}

interface TaskAssigneeSelectorProps {
  ptaId: string | null;
  members: Member[];
  selectedAssignees: string[];
  selectedRoles: string[];
  onAssigneesChange: (assignees: string[]) => void;
  onRolesChange: (roles: string[]) => void;
  includeMe?: boolean;
}

const ROLE_DISPLAY_NAMES: Record<string, string> = {
  chair: "Chair",
  secretary: "Secretary",
  treasurer: "Treasurer",
  trustee: "Trustee",
  events: "Events",
  community: "Community",
  catering: "Catering",
  assets: "Assets",
  shop: "Shop",
  school_liaison: "School Liaison",
};

export function TaskAssigneeSelector({
  ptaId,
  members,
  selectedAssignees,
  selectedRoles,
  onAssigneesChange,
  onRolesChange,
  includeMe = true,
}: TaskAssigneeSelectorProps) {
  const [rolesWithMembers, setRolesWithMembers] = useState<RoleWithMembers[]>([]);
  const [activeTab, setActiveTab] = useState("roles");

  useEffect(() => {
    if (ptaId) {
      fetchRolesWithMembers();
    }
  }, [ptaId]);

  const fetchRolesWithMembers = async () => {
    if (!ptaId) return;

    // Get all members with roles for this PTA
    const { data, error } = await supabase
      .from("user_roles")
      .select(`
        role,
        user_id
      `);

    if (error || !data) return;

    // Get member info for these users within the PTA
    const roleMap: Record<string, { id: string; full_name: string }[]> = {};

    for (const ur of data) {
      // Find the member in our members list that matches this user_id
      const member = members.find(m => m.user_id === ur.user_id);
      if (member && member.user_id) {
        const role = ur.role as string;
        if (!roleMap[role]) {
          roleMap[role] = [];
        }
        // Avoid duplicates - use user_id for task assignment
        if (!roleMap[role].some(m => m.id === member.user_id)) {
          roleMap[role].push({ id: member.user_id, full_name: member.full_name });
        }
      }
    }

    // Convert to array and filter to only show roles we want to display
    const displayRoles = Object.keys(ROLE_DISPLAY_NAMES);
    const rolesArray: RoleWithMembers[] = displayRoles
      .filter(role => roleMap[role] && roleMap[role].length > 0)
      .map(role => ({
        role,
        members: roleMap[role],
      }));

    setRolesWithMembers(rolesArray);
  };

  const handleRoleToggle = (role: string, checked: boolean) => {
    if (checked) {
      onRolesChange([...selectedRoles, role]);
    } else {
      onRolesChange(selectedRoles.filter(r => r !== role));
    }
  };

  const handleAssigneeToggle = (assigneeId: string, checked: boolean) => {
    if (checked) {
      onAssigneesChange([...selectedAssignees, assigneeId]);
    } else {
      onAssigneesChange(selectedAssignees.filter(id => id !== assigneeId));
    }
  };

  return (
    <div className="space-y-2">
      <Label>Assign To</Label>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="roles">Roles</TabsTrigger>
          <TabsTrigger value="individual">Individual</TabsTrigger>
        </TabsList>
        
        <TabsContent value="roles" className="mt-2">
          <div className="border rounded p-2 max-h-48 overflow-y-auto">
            {rolesWithMembers.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No roles with active members found</p>
            ) : (
              <div className="space-y-2">
                {rolesWithMembers.map((roleData) => (
                  <div key={roleData.role} className="flex items-start space-x-2 p-1 hover:bg-muted/50 rounded">
                    <Checkbox
                      id={`role-${roleData.role}`}
                      checked={selectedRoles.includes(roleData.role)}
                      onCheckedChange={(checked) => handleRoleToggle(roleData.role, !!checked)}
                    />
                    <div className="flex-1">
                      <Label htmlFor={`role-${roleData.role}`} className="text-sm cursor-pointer font-medium">
                        {ROLE_DISPLAY_NAMES[roleData.role] || roleData.role}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {roleData.members.length === 1 
                          ? roleData.members[0].full_name
                          : `${roleData.members.length} members: ${roleData.members.map(m => m.full_name).join(", ")}`
                        }
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="individual" className="mt-2">
          <div className="border rounded p-2 max-h-48 overflow-y-auto">
            <div className="grid grid-cols-2 gap-2">
              {includeMe && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="assign-me"
                    checked={selectedAssignees.includes('me')}
                    onCheckedChange={(checked) => handleAssigneeToggle('me', !!checked)}
                  />
                  <Label htmlFor="assign-me" className="text-sm cursor-pointer">ME</Label>
                </div>
              )}
              {members.filter(m => m.user_id).map((member) => (
                <div key={member.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`assign-${member.id}`}
                    checked={selectedAssignees.includes(member.user_id!)}
                    onCheckedChange={(checked) => handleAssigneeToggle(member.user_id!, !!checked)}
                  />
                  <Label htmlFor={`assign-${member.id}`} className="text-sm cursor-pointer">
                    {member.full_name}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      {(selectedRoles.length > 0 || selectedAssignees.length > 0) && (
        <p className="text-xs text-muted-foreground">
          {selectedRoles.length > 0 && `Roles: ${selectedRoles.map(r => ROLE_DISPLAY_NAMES[r] || r).join(", ")}`}
          {selectedRoles.length > 0 && selectedAssignees.length > 0 && " | "}
          {selectedAssignees.length > 0 && `Individuals: ${selectedAssignees.includes('me') ? 'Me' : ''}${selectedAssignees.filter(a => a !== 'me').length > 0 ? (selectedAssignees.includes('me') ? ', ' : '') + selectedAssignees.filter(a => a !== 'me').length + ' member(s)' : ''}`}
        </p>
      )}
    </div>
  );
}

export { ROLE_DISPLAY_NAMES };
export type { RoleWithMembers };
